export const now = (): number =>
  Math.round((performance.timeOrigin + performance.now()) * 10) / 10;

/**
 * A v4 UUID. Ingest, the score Lambda and the results API all expect UUID-shaped IDs,
 * so browsers without crypto.randomUUID (older Safari, non-secure pages) still get one.
 */
export const randomId = (): string => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

/**
 * Stable, value-free description of an element: a `data-ac` label if the page
 * provides one, else tag + id/name/type. Never text content or values.
 */
export const describe = (target: EventTarget | null): string | undefined => {
  if (!target || typeof (target as Element).closest !== 'function') return undefined;
  const el = target as Element;
  const labelled = el.closest('[data-ac]');
  if (labelled) return labelled.getAttribute('data-ac') ?? undefined;
  let d = el.tagName.toLowerCase();
  if (el.id) d += `#${el.id}`;
  const name = el.getAttribute('name');
  if (name) d += `[name=${name}]`;
  const type = el.getAttribute('type');
  if (type && el.tagName === 'INPUT') d += `[type=${type}]`;
  return d;
};

const INTERACTIVE = 'a,button,input,select,textarea,label,summary,[role=button],[role=link],[data-ac]';

/**
 * Where a click landed relative to the centre of the control it hit, normalised to
 * -1..1 per axis. Agents that click computed centres land at ~0,0 every time.
 */
export const clickOffset = (
  target: EventTarget | null,
  x: number,
  y: number,
): { dx: number; dy: number; w: number; h: number } | undefined => {
  if (!target || typeof (target as Element).closest !== 'function') return undefined;
  const el = (target as Element).closest(INTERACTIVE) ?? (target as Element);
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return undefined;
  const round = (n: number) => Math.round(n * 1000) / 1000;
  return {
    dx: round((x - (r.left + r.width / 2)) / (r.width / 2)),
    dy: round((y - (r.top + r.height / 2)) / (r.height / 2)),
    w: Math.round(r.width),
    h: Math.round(r.height),
  };
};

const NAMED_KEYS = new Set([
  'Backspace', 'Delete', 'Enter', 'Tab', 'Escape', 'Shift', 'Control', 'Alt', 'Meta',
  'CapsLock', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
  'PageUp', 'PageDown',
]);

/** Printable keys collapse to "char" so nothing typed can be reconstructed. */
export const keyClass = (key: string | undefined): string => {
  if (!key) return 'none';
  if (key.length === 1) return 'char';
  if (NAMED_KEYS.has(key)) return key.toLowerCase();
  if (key === 'Unidentified' || key === 'Process') return key.toLowerCase();
  return 'other';
};

// A path segment that looks like an identifier: numbers, UUIDs/hex, emails, tokens mixing
// letters and digits, Alloy tokens (a short uppercase prefix, a dash, random base62, often
// with no digit: J-..., JA-...), or long mixed-case strings
const ID_LIKE = /^(\d+|[0-9a-f-]{8,}|.*@.*|(?=.*\d)[A-Za-z0-9_-]{8,}|.{25,})$/i;
const ALLOY_TOKEN = /^[A-Z]{1,3}-[A-Za-z0-9]{8,}$/;
// At least 3 capitals, so camelCase route words (listEntities) stay
const MIXED_CASE = /^(?=(?:.*[A-Z]){3})(?=.*[a-z])[A-Za-z0-9]{12,}$/;
const isIdLike = (s: string): boolean => ID_LIKE.test(s) || ALLOY_TOKEN.test(s) || MIXED_CASE.test(s);

/**
 * The route template for a path when the page doesn't supply one: every ID-like segment
 * becomes ":id", so raw tokens and emails in URLs are never sent.
 */
export const templatePath = (path: string): string =>
  path.split('/').map((s) => (s && isIdLike(decodeSegment(s)) ? ':id' : s)).join('/');

const decodeSegment = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};
